import type {Object3D} from "three";
import {Constants} from "../Constants";
import {LeaderLine} from "../elements3d/LeaderLine";
import type {SpaceViewRenderer} from "../renderers/SpaceViewRenderer";
import {LinkLineArrow} from "../elements3d/LinkLineArrow";
import {THREEUtils} from "../../../../../../utils/THREEUtils";
import {Collection} from "../../../../../../data/models/abstract/Collection";
import type {IModel} from "../../../../../../data/models/Model";
import type {ISpaceItemMinimumData, SpaceItem} from "../elements3d/SpaceItem";
import type {ILineSet} from "../../../../../5.0/modules/spaceeditor/links/LinkLineActionBarV5";
import type {LinkPropertyDto} from "../../../../../../generated/api/base";
import {StringUtils} from "../../../../../../utils/data/string/StringUtils";
import {ItemManager} from "./spaceitems/ItemManager";

export class LinkLineManager extends ItemManager {
	private _lines: LeaderLine[] = [];
	private _currentXyiconIds: Set<string> = new Set<string>();
	public readonly lineColor: string = `#${Constants.COLORS.PICTON_BLUE}`;
	protected _items: Collection<LinkLineArrow> = new Collection();
	private _onCloseDetailsPanel?: () => void; // Callback to close details panel
	private _onUpdateActionBar?: () => void; // Callback to update action bar
	private _allLinks: Collection<LinkLineArrow> = new Collection(); // Store all links
	private _visibleLinksByXyiconPair: Map<string, string> = new Map(); // Track which link is visible for each xyicon pair

	constructor(spaceViewRenderer: SpaceViewRenderer) {
		super(spaceViewRenderer, "link");
	}

	// Helper method to generate a unique key for xyicon pairs (order-independent)
	private getXyiconPairKey(xyiconId1: string, xyiconId2: string): string {
		return xyiconId1 < xyiconId2 ? `${xyiconId1}-${xyiconId2}` : `${xyiconId2}-${xyiconId1}`;
	}

	// Get the most recent link between two xyicons based on lastModifiedAt or linkID as fallback
	private getMostRecentLinkForPair(links: LinkLineArrow[]): LinkLineArrow {
		return links.toSorted((a, b) => {
			const aModifiedAt = a.link.data.lastModifiedAt ? new Date(a.link.data.lastModifiedAt).getTime() : 0;
			const bModifiedAt = b.link.data.lastModifiedAt ? new Date(b.link.data.lastModifiedAt).getTime() : 0;
			if (aModifiedAt !== bModifiedAt) {
				return bModifiedAt - aModifiedAt; // Newest first
			}
			// Fallback to linkID comparison if dates are equal or missing
			return StringUtils.sortIgnoreCase(a.link.id ?? "", b.link.id ?? "");
		})[0];
	}

	// Update visibility for all links between the same xyicon pairs
	private updateLinkVisibility(): void {
		// Group all links by xyicon pairs
		const linksByPair = new Map<string, LinkLineArrow[]>();

		for (const link of this._allLinks.array) {
			const pairKey = this.getXyiconPairKey(link.link.objectAID, link.link.objectBID);
			if (!linksByPair.has(pairKey)) {
				linksByPair.set(pairKey, []);
			}
			linksByPair.get(pairKey)!.push(link);
		}

		// Clear current visible items and visibility tracking
		this._items.replaceByArray([]);
		this._visibleLinksByXyiconPair.clear();

		// For each pair, show only the most recent link
		for (const [pairKey, links] of linksByPair) {
			const mostRecentLink = this.getMostRecentLinkForPair(links);

			// Hide all links in this pair first
			for (const link of links) {
				link.floorContainer.visible = false;
			}

			// Show only the most recent link
			mostRecentLink.floorContainer.visible = true;
			this._items.add(mostRecentLink);
			this._visibleLinksByXyiconPair.set(pairKey, mostRecentLink.link.id);
		}

		this._spaceViewRenderer.needsRender = true;
	}

	public destroyAll() {
		// Dispose regular lines (V4)
		for (const line of this._lines) {
			line.destroy();
		}

		// Dispose arrow lines using their built-in dispose method (V5)
		for (const arrowLine of this._allLinks.array) {
			arrowLine.dispose();
		}
		// Clear arrays
		this._lines.length = 0;
		this._items.replaceByArray([]); //  Use inherited collection
		this._allLinks.replaceByArray([]); // Clear all links collection
		this._visibleLinksByXyiconPair.clear(); // Clear visibility tracking
		this._currentXyiconIds.clear();
		this._spaceViewRenderer.needsRender = true;
	}

	public update(lineSets: ILineSet[]) {
		this.destroyAll(); // Use destroyAll to properly clean up both V4 and V5 lines

		// V4: Use LeaderLine (original dashed lines)
		if (this._spaceViewRenderer.transport.appState.currentUIVersion !== "5.0") {
			// V4 implementation - use original LeaderLine approach
			for (const lineSet of lineSets) {
				const {fromXyicon, toXyicons} = lineSet;

				this._currentXyiconIds.add(fromXyicon.modelData.id);
				toXyicons.forEach((x) => this._currentXyiconIds.add(x.modelData.id));
				const fromPosition = fromXyicon.position;

				for (const toXyicon of toXyicons) {
					const toPosition = toXyicon.position;
					const dx = toPosition.x - fromPosition.x;
					const dy = toPosition.y - fromPosition.y;
					const line = new LeaderLine(
						this._spaceViewRenderer,
						fromXyicon.group,
						dx,
						dy,
						fromXyicon.group,
						parseInt(this.lineColor.replace("#", "0x")),
						false,
					);

					line.line.rotateZ(-fromXyicon.orientation);
					THREEUtils.updateMatrices(line.line);
					this._lines.push(line);
					if (line.isVisible) {
						fromXyicon.addGroupToSceneIfNeeded();
					}
				}
			}
			return;
		}

		// V5: Use LinkLineArrow (new arrow lines)
		for (const lineSet of lineSets) {
			const {fromXyicon, toXyicons, link} = lineSet;
			this._currentXyiconIds.add(fromXyicon.modelData.id);
			toXyicons.forEach((x) => this._currentXyiconIds.add(x.modelData.id));

			for (const toXyicon of toXyicons) {
				const uniArrow = new LinkLineArrow(fromXyicon, toXyicon, fromXyicon.group, this._spaceViewRenderer, link, link.linkProperties);
				THREEUtils.updateMatrices(uniArrow.floorContainer);
				this._allLinks.add(uniArrow); // Store all links in _allLinks collection
				fromXyicon.addGroupToSceneIfNeeded();
			}
		}

		// After creating all links, update visibility to show only the most recent link per xyicon pair
		this.updateLinkVisibility();

		// Ensure the space is re-rendered after creating new link lines
		this._spaceViewRenderer.needsRender = true;
	}

	public onCanvasResize() {
		for (const line of this._allLinks.array) {
			line.onCanvasResize();
		}
	}

	public mouseOutAll() {
		for (const line of this._items.array) {
			line.mouseOut();
		}
	}

	public hasSelectedLines(): boolean {
		return this.selectedItems.length > 0; // Use inherited selectedItems
	}

	public get currentXyiconIds() {
		return this._currentXyiconIds;
	}

	public get lines() {
		return this._lines;
	}

	public get arrowHeadLines() {
		return this._items.array; // Use inherited collection
	}

	public getSelectedLink(): LinkLineArrow | null {
		return this._items.array.find((item) => item.isSelected) || null;
	}

	public getIntersectables(): Object3D[] {
		const intersectables: Object3D[] = [];

		// Add arrow head lines (V5) - return the actual intersectable meshes
		for (const arrowLine of this._items.array) {
			const arrowIntersectables = arrowLine.getIntersectables();
			intersectables.push(...arrowIntersectables);
		}

		// Add regular lines (V4)
		for (const line of this._lines) {
			intersectables.push(line.line);
		}

		return intersectables;
	}

	public createSpaceItem3DFromModel(data: ISpaceItemMinimumData): Promise<SpaceItem> {
		throw new Error("Link lines are created through update method, not from model");
	}

	public addItemsByModel(model: IModel[]): Promise<SpaceItem[]> {
		throw new Error("Link lines are created through update method, not from model");
	}

	protected addItems(items: LinkLineArrow[], addToServer: boolean = true): Promise<void> {
		this._items.addMultiple(items);
		return Promise.resolve();
	}

	public updateItems(items: SpaceItem[], force?: boolean, type?: "position" | "orientation"): Promise<void> {
		// Link lines don't need position/orientation updates
		return Promise.resolve();
	}

	public updateLinkProperties(linkId: string, linkProperties: LinkPropertyDto): void {
		// Find the link line with the specified link ID and update its properties
		// Search in all links since the target link might not be currently visible
		for (const arrowLine of this._allLinks.array) {
			if (arrowLine.link.id === linkId) {
				arrowLine.updateLinkProperties(linkProperties);

				// If this link became visible due to property changes, update visibility
				const pairKey = this.getXyiconPairKey(arrowLine.link.objectAID, arrowLine.link.objectBID);
				const currentVisibleId = this._visibleLinksByXyiconPair.get(pairKey);

				// If this is not the currently visible link for this pair, check if it should become visible
				// (This could happen if properties changed the lastModifiedAt timestamp)
				if (currentVisibleId !== linkId) {
					this.updateLinkVisibility();
				}
				break;
			}
		}

		// Trigger action bar update if callback is set
		if (this._onUpdateActionBar) {
			this._onUpdateActionBar();
		}
	}

	// Set callback function for closing details panel
	public setCloseDetailsPanelCallback(callback: () => void) {
		this._onCloseDetailsPanel = callback;
	}

	// Clear callback function
	public clearCloseDetailsPanelCallback() {
		this._onCloseDetailsPanel = undefined;
	}

	// Set callback function for updating action bar
	public setUpdateActionBarCallback(callback: () => void) {
		this._onUpdateActionBar = callback;
	}

	// Clear callback function
	public clearUpdateActionBarCallback() {
		this._onUpdateActionBar = undefined;
	}

	// Override deselectAll to also close details panel when link lines are deselected
	public override deselectAll() {
		const hadSelectedLinks = this._items.array.some((arrowLine) => arrowLine.isSelected);

		// Deselect all visible links
		for (const arrowLine of this._items.array) {
			arrowLine.deselect();
		}

		// Call parent deselectAll to handle any other item deselection
		super.deselectAll();

		if (hadSelectedLinks && this._onCloseDetailsPanel) {
			this._onCloseDetailsPanel();
		}
	}
}
